import { createContext, useContext } from 'react';
import { useTranslation } from 'next-i18next';
import { TRIAL_DAYS } from '@/data/constants';
import { trialDaysText } from '@/helpers/trialDays';
import usePublicSetting from '@/hooks/usePublicSetting';

/**
 * The number of the days of the trial that comes with the props of the page from the server (_app). It is a context, not the jotai store:
 * the store of the server is shared by all the requests and keeps the first value.
 */
export const TrialDaysContext = createContext<number | undefined>(undefined);

/**
 * The duration of the trial in days (the setting `trial.durationDays`, FOR-2710, stage 2g).
 * On the pages of the site the number comes with the data of the page from the server (the same on the server and in the browser);
 * where there is no such data (the widgets) it is read from the public settings, until then the default value is used.
 */
export const useTrialDays = (): number => {
  const fromPage = useContext(TrialDaysContext);
  const setting = usePublicSetting('trial.durationDays', TRIAL_DAYS);
  return fromPage ?? setting;
};

/** The days with the right form of the word for the texts written in Russian in the code: "7 дней", "3 дня" */
export const useTrialDaysText = () => trialDaysText(useTrialDays());

/** The days with the right form of the word in the language of the page (the texts of the locale files): "7 дней" / "7 days" */
export const useTrialDaysLocaleText = () => {
  const { t } = useTranslation();
  return t('trial_days', { count: useTrialDays() });
};
